import { authFetch, fetchWithAuth, BASE_BACKEND_URL } from '../auth'
import React, { useState, useContext, useEffect, useRef } from 'react'
import cookie from 'react-cookies'
import PosTaskEffectivityView from './PosTaskEffectivityView'
import PosTaskEffectivityContext from './context/PosTaskEffectivityContext'
import usePagination from '../layout/usePagination'
import { DEFAULT_PER_PAGE } from '../constants/constant'
import { LayoutContext } from '../layout/MainLayout'
import LoadingScreen from '../components/LoadingScreen'

const PosTaskEffectivityApp = () => {
  const { collapsed } = useContext(LayoutContext)

  const [clientsData, setClientsData] = useState(null)
  const [tasksData, setTasksData] = useState(null)
  const [fetchDataStatus, setFetchDataStatus] = useState({
    filters: false,
    tasks: false
  })

  const [filterQuery, setFilterQuery] = useState(null)
  const [searchQuery, setSearchQuery] = useState(null)
  const [dateQuery, setDateQuery] = useState(null)

  const { pagination, setPagination, handlePaginationClick } = usePagination({
    per_page: DEFAULT_PER_PAGE
  })

  const [title, setTitle] = useState('Efectividad en punto de venta')

  const abortController = useRef(new AbortController())

  const abortCurrentFetchs = () => {
    abortController.current.abort('Fetch Aborted by User.')
    abortController.current = new AbortController()
  }

  useEffect(() => {
    window.addEventListener('beforeunload', abortCurrentFetchs)
    return () => window.removeEventListener('beforeunload', abortCurrentFetchs)
  }, [])

  useEffect(() => {
    fetchClientsFromServer()
  }, [])

  useEffect(() => {
    updateURLPaginationData()
    fetchTasksFromServer()
  }, [pagination.current_page, filterQuery, searchQuery, dateQuery])

  const updateURLPaginationData = () => {
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set('page', pagination.current_page)

    const newUrl = `${window.location.pathname}?${searchParams.toString()}`
    window.history.replaceState(null, '', newUrl)
  }

  const fetchClientsFromServer = async () => {
    setFetchDataStatus((prev) => ({ ...prev, filters: true }))

    try {
      const response = await fetchWithAuth(
        `${BASE_BACKEND_URL}/pos_task_activities?client_names_only=true&business_tenant_uid=${cookie.load('business_tenant_uid')}`,
        {
          method: 'GET',
          signal: abortController.current.signal
        }
      )

      if (!response || response.error) {
        console.error('Error loading clients', response)
      } else {
        setClientsData(response.result || [])
      }
    } catch (error) {
      if (error !== 'Fetch Aborted by User.') console.error(error)
    } finally {
      setFetchDataStatus((prev) => ({ ...prev, filters: false }))
    }
  }

  const fetchTasksFromServer = async () => {
    setFetchDataStatus((prev) => ({ ...prev, tasks: true }))

    const per_page = pagination.per_page
    const page = pagination.current_page

    try {
      const response = await authFetch(
        `${BASE_BACKEND_URL}/pos_task_activities?page=${page}&per_page=${per_page}${
          filterQuery || dateQuery || searchQuery ? '&filter_search=true' : ''
        }${filterQuery || ''}${dateQuery || ''}${
          searchQuery || ''
        }&business_tenant_uid=${cookie.load('business_tenant_uid')}`,
        {
          method: 'GET',
          signal: abortController.current.signal,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      )

      if (!response.ok) {
        console.error('Fetch tasks failed', response)
        return
      }

      const data = await response.json()

      setPagination((prev) => ({
        ...prev,
        total_pages: Math.ceil(data.count / per_page)
      }))

      setTasksData(data.result || [])
    } catch (error) {
      if (error !== 'Fetch Aborted by User.') console.error(error)
    } finally {
      setFetchDataStatus((prev) => ({ ...prev, tasks: false }))
    }
  }

  const isLoadingAllData = clientsData === null || tasksData === null

  const isLoading = fetchDataStatus.filters || fetchDataStatus.tasks

  if (isLoadingAllData) {
    return <LoadingScreen text="Cargando ..." />
  }

  return (
    <PosTaskEffectivityContext.Provider
      value={{
        fetchDataStatus,
        clientsData,
        tasksData,
        pagination,
        setFilterQuery,
        setDateQuery,
        setSearchQuery,
        filterQuery,
        dateQuery,
        searchQuery,
        fetchClientsFromServer,
        fetchTasksFromServer,
        handlePaginationClick,
        title,
        setPagination,
        isLoading
      }}
    >
      <PosTaskEffectivityView collapsed={collapsed} />
    </PosTaskEffectivityContext.Provider>
  )
}

export default PosTaskEffectivityApp
